import React, { createContext, useState, useEffect, useMemo, useCallback } from "react";
import { apiService, setSignalRToken, getSignalRToken, ZDARZENIE_HASLO_DO_ZMIANY } from "../api/apiService";
import { useNavigate } from "react-router-dom";
import { localizedPath } from "../hooks/useLocalizedNavigate";
import { clearSensitiveHrStorage } from "../utils/sensitiveStorage";
// [PII-09] Moduł bez importów — nie zamyka cyklu z tym kontekstem.
import { KLUCZ_HISTORII } from "../utils/historiaWyszukiwania";
// Pamięć zasięgu „Mój Dział" — czyszczona przy wylogowaniu i przy zmianie uprawnień (patrz `logout`).
// Import idzie W JEDNĄ STRONĘ: `useMojDzialZasieg` nie zna tego kontekstu, więc cyklu nie ma.
import { wyczyscPamiecZasiegu } from "../hooks/useMojDzialZasieg";

const AuthContext = createContext();

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
   PRAWDZIWA PRZYCZYNA ODMOWY LOGOWANIA — WIDOCZNA DLA CZŁOWIEKA, ZAPISANA DLA IT
   [zgłoszenie 2026-09-13, §12.4: „front połyka prawdziwą przyczynę błędu logowania przez Microsoft"]

   CO BYŁO ŹLE — DWIE WARSTWY, ZMIERZONE NA KODZIE, NIE ZGADNIĘTE:

   1) ŻĄDANIE LOGOWANIA BYŁO TRAKTOWANE JAK WYGASŁA SESJA. Interceptor w `apiService` na KAŻDE 401
      (poza `/users/refresh` i integracjami Microsoftu) wysyła `POST /users/refresh`, a gdy to się
      nie udaje — odrzuca obietnicę BŁĘDEM ODŚWIEŻANIA, nie pierwotnym. Na ekranie logowania nie ma
      czego odświeżać, więc `/users/refresh` oddaje `400 "Access token and refresh token are
      required."` i TO lądowało w rękach `login`/`loginOffice365`.
      ZMIERZONE: przy 401 „Konto jest zablokowane." z `/users/login` wywołujący dostawał
      `{ success:false, message:"Access token and refresh token are required." }` — i dokładnie ten
      angielski napis o tokenach odświeżania pokazywał się pracownikowi, który pomylił hasło.

   2) `loginOffice365` WYRZUCAŁ BŁĄD DO KOSZA: `catch (err) { return { success:false,
      message:"Błąd logowania przez Microsoft" } }` — bez statusu, bez treści z serwera, bez
      `console.error`. Backend ma gotowe, polskie powody odmowy („Konto jest zablokowane.",
      „Konfiguracja dla tego tenanta O365 jest nieaktywna lub nie istnieje.", „Nie rozpoznaliśmy
      konta w aplikacji…", „To konto jest kontem gościa (B2B)…") i KAŻDY z nich logował po swojej
      stronie — ale do przeglądarki nie docierał ŻADEN.
      SKUTEK: człowiek nie wiedział, czy to złe hasło, brak zgody administratora, wygasły sekret
      czy zablokowane konto; IT nie miało czego szukać w logu serwera.

   JAK JEST TERAZ:
   • `KANAL_LOGOWANIA` — żądania logowania jawnie wyłączają gałąź odświeżania sesji, więc pierwotna
     odpowiedź serwera (status + treść) dochodzi nietknięta;
   • powód z serwera idzie NA EKRAN (backendowe komunikaty są po polsku i pisane dla człowieka);
   • pełny ślad techniczny idzie do DZIENNIKA PRZEGLĄDARKI (`console.error`) i do pamięci
     przeglądarki (ostatnie 5 odmów) — żeby przeżył przeładowanie strony po powrocie z Microsoftu;
   • CO NIE WYCHODZI: token tożsamości, hasło, sekret aplikacji ani żaden inny długi ciąg — filtr
     `bezSekretow` czyści treść, zanim cokolwiek zobaczy ekran, konsola albo pamięć przeglądarki.
     Pokazujemy POWÓD, nie materiał do podszycia się.
   ═══════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Konfiguracja żądania logowania.
 *
 * `_retry` to flaga, którą interceptor `apiService` czyta jako „tego żądania NIE odświeżaj sesją":
 * gałąź `POST /users/refresh` wykonuje się wyłącznie przy `!originalRequest._retry`. Dla logowania
 * jest to prawda wprost — nie ma jeszcze sesji, którą dałoby się odświeżyć.
 */
const KANAL_LOGOWANIA = { _retry: true };

/** Pamięć przeglądarki ze śladem ostatnich odmów logowania (bez sekretów) — dla działu IT. */
export const KLUCZ_DIAGNOSTYKI_LOGOWANIA = "itsm_diagnostyka_logowania";
/** Ile odmów trzymamy. Więcej nie jest potrzebne, a pamięć przeglądarki nie jest archiwum. */
const ILE_SLADOW_ODMOWY = 5;

/**
 * Zdejmuje z tekstu wszystko, co mogłoby posłużyć do podszycia się, i skraca go do długości,
 * która mieści się w jednym komunikacie.
 *
 * Kolejność reguł ma znaczenie: najpierw token w formacie JWT (trzy członki po kropkach), potem
 * dowolny długi ciąg bez spacji (klucz, skrót, identyfikator sesji), na końcu pary
 * `nazwa: wartość` dla nazw, które same zapowiadają sekret. Zwykłe zdanie po polsku nie ma 40
 * znaków bez spacji, więc nie ucierpi.
 */
const WZORZEC_TOKENU = /eyJ[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}(?:\.[A-Za-z0-9_-]+)?/g;

/** Sam token (JWT) — bez reguły „długi ciąg", która zjadałaby identyfikatory śladu. */
const bezTokenow = (wartosc) =>
  (typeof wartosc === "string" ? wartosc.replace(WZORZEC_TOKENU, "[token usunięty]") : "");

const bezSekretow = (wartosc) => {
  if (typeof wartosc !== "string" || !wartosc.trim()) return "";
  let t = bezTokenow(wartosc)
    .replace(/[A-Za-z0-9+/_-]{40,}={0,2}/g, "[dane usunięte]")
    .replace(
      /("?(?:password|haslo|hasło|secret|client_secret|token|id_token|access_token|refresh_token|authorization|cookie)"?\s*[:=]\s*)("[^"]*"|[^\s,&;}]+)/gi,
      "$1[usunięte]",
    );
  t = t.replace(/\s+/g, " ").trim();
  return t.length > 300 ? `${t.slice(0, 300)}…` : t;
};

/** Powód odmowy z ciała odpowiedzi: backend odmawia gołym tekstem, ale przy 500/walidacji bywa obiekt. */
const powodZOdpowiedzi = (dane) => {
  if (typeof dane === "string") return bezSekretow(dane);
  if (dane && typeof dane === "object") {
    const kandydat = dane.message || dane.error || dane.detail || dane.title;
    if (typeof kandydat === "string") return bezSekretow(kandydat);
  }
  return "";
};

/** Identyfikator śladu — po nim IT znajdzie TĘ próbę w logu serwera albo w Entra. */
const identyfikatorSladu = (blad) => {
  const dane = blad && blad.response ? blad.response.data : null;
  const naglowki = (blad && blad.response && blad.response.headers) || {};
  const czytaj = (nazwa) => {
    try { return typeof naglowki.get === "function" ? naglowki.get(nazwa) : naglowki[nazwa]; }
    catch { return null; }
  };
  const kandydat =
    (dane && typeof dane === "object" && (dane.traceId || dane.correlationId))
    || (blad && blad.correlationId)
    || czytaj("x-correlation-id") || czytaj("x-request-id") || czytaj("traceparent")
    || null;
  /*
   * ⚠️ Tu NIE wolno użyć `bezSekretow`: reguła „ciąg 40+ znaków bez spacji" zjadłaby cały
   * nagłówek `traceparent` (`00-<32 znaki>-<16 znaków>-01` to 55 znaków) — czyli dokładnie to,
   * po czym IT znajduje tę próbę w logu. Token i tak wycinamy, a długość tniemy do 80 znaków.
   */
  return typeof kandydat === "string" && kandydat.trim() ? bezTokenow(kandydat).trim().slice(0, 80) : null;
};

/**
 * Opis odmowy logowania gotowy do pokazania działowi IT. Wołany też przez `LoginPage` dla etapu
 * „microsoft" (błędy biblioteki MSAL, które nie dotarły nawet do naszego serwera).
 *
 * @param {*} blad wyjątek axios, błąd MSAL albo sam tekst
 * @param {{etap?:"serwer"|"microsoft", zasob?:string|null, kod?:string|null}} kontekst
 */
export const opiszOdmoweLogowania = (blad, kontekst = {}) => {
  const { etap = "serwer", zasob = null, kod = null } = kontekst;
  const odpowiedz = blad && blad.response ? blad.response : null;
  const powodTekstowy = typeof blad === "string" ? bezSekretow(blad) : "";
  return {
    etap,
    znacznik: new Date().toISOString(),
    status: odpowiedz && typeof odpowiedz.status === "number" ? odpowiedz.status : null,
    kod: kod || (blad && (blad.errorCode || blad.code)) || null,
    zasob: zasob || (blad && blad.config && blad.config.url) || null,
    /*
     * `powod` — POKAZYWANY. Wyłącznie to, co napisał NASZ serwer (polskie zdania dla człowieka)
     * albo nasz własny opis tekstowy.
     */
    powod: powodZOdpowiedzi(odpowiedz ? odpowiedz.data : null) || powodTekstowy || null,
    /*
     * `powodTechniczny` — TYLKO DZIENNIK I PAMIĘĆ PRZEGLĄDARKI, nigdy ekran. Treść wyjątku
     * biblioteki Microsoftu to angielska instrukcja dla programisty („Hash value cannot be
     * processed… verify that your redirectUri…"); pracownik hali wyciągał z niej błędne wnioski,
     * dlatego na ekranie stoi polskie zdanie + KOD błędu, a pełny tekst zostaje dla IT.
     * Zapadka na tę regułę: pages/__tests__/logowanieO365Przekierowaniem.test.js.
     */
    powodTechniczny:
      bezSekretow(blad && blad.errorMessage) || bezSekretow(blad && blad.message) || null,
    identyfikator: identyfikatorSladu(blad),
  };
};

/** Zdanie dla człowieka. Powód z serwera jest już po polsku — fallback tylko gdy serwer milczy. */
export const komunikatOdmowyLogowania = (blad, etap = "serwer") => {
  const status = blad && blad.response && typeof blad.response.status === "number" ? blad.response.status : null;
  const powod = powodZOdpowiedzi(blad && blad.response ? blad.response.data : null);
  if (powod) return powod;
  if (status === null) {
    return "Brak połączenia z serwerem mPracownika — logowanie nie doszło do końca. Sprawdź połączenie i spróbuj ponownie.";
  }
  if (status === 429) return "Zbyt wiele prób logowania. Odczekaj chwilę i spróbuj ponownie.";
  if (status >= 500) {
    return `Serwer mPracownika nie dokończył logowania (odpowiedź ${status}). Zgłoś to do działu IT.`;
  }
  if (etap === "microsoft") return "Logowanie przez Office 365 nie powiodło się.";
  return `Logowanie zostało odrzucone przez serwer (odpowiedź ${status}).`;
};

/** Ślad odmowy w pamięci przeglądarki — przeżywa przeładowanie strony po powrocie z Microsoftu. */
export const zapiszSladOdmowyLogowania = (diagnostyka) => {
  try {
    const surowe = localStorage.getItem(KLUCZ_DIAGNOSTYKI_LOGOWANIA);
    const poprzednie = surowe ? JSON.parse(surowe) : [];
    const lista = Array.isArray(poprzednie) ? poprzednie : [];
    lista.unshift(diagnostyka);
    localStorage.setItem(KLUCZ_DIAGNOSTYKI_LOGOWANIA, JSON.stringify(lista.slice(0, ILE_SLADOW_ODMOWY)));
  } catch { /* tryb prywatny albo brak miejsca — ślad w konsoli i na ekranie zostaje */ }
};

/** Dziennik przeglądarki: jedna linia do odczytania przez telefon + obiekt do rozwinięcia. */
export const zglosOdmoweLogowania = (diagnostyka) => {
  const gdzie = diagnostyka.etap === "microsoft" ? "Microsoft" : "serwer mPracownika";
  console.error(
    `[LOGOWANIE] odmowa (${gdzie}): ${diagnostyka.status === null ? "brak odpowiedzi" : diagnostyka.status}`
    + ` ${diagnostyka.zasob || ""} ${diagnostyka.kod ? `kod=${diagnostyka.kod}` : ""}`
    + ` — ${diagnostyka.powod || diagnostyka.powodTechniczny || "bez treści"}`,
    diagnostyka,
  );
};

/** Jedna droga wyjścia dla KAŻDEJ nieudanej próby logowania: ekran + dziennik + ślad. */
const odmowaLogowania = (blad, etap, zasob) => {
  const diagnostyka = opiszOdmoweLogowania(blad, { etap, zasob });
  zglosOdmoweLogowania(diagnostyka);
  zapiszSladOdmowyLogowania(diagnostyka);
  return { success: false, message: komunikatOdmowyLogowania(blad, etap), diagnostyka };
};

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
   [WYDAJNOŚĆ 2026-09-12] WIDOCZNOŚĆ MODUŁÓW — JEDNO POBRANIE NA SESJĘ, NIE TRZY NA KAŻDE WEJŚCIE.

   CO ZMIERZYŁEM NA PRODUKCJI (dziennik usługi, 3 dni, 49 863 żądania GET): `GET /api/module-visibility`
   poszło 385 razy — TYLE SAMO, co lista zgłoszeń (`/api/tickets`, 387), mimo że jest to tabela
   przełączników, którą administrator rusza raz na tydzień. Powód: o widoczność pyta NIEZALEŻNIE
   TRZECH konsumentów powłoki — ten kontekst (blokada tras), `Sidebar` (menu boczne) i `UIContext`
   (widget „Nowe zgłoszenie") — a każdy z nich ma efekt zawieszony na tożsamości `user`. Tożsamość
   ta zmienia się co najmniej DWA RAZY przy każdym wejściu do aplikacji: raz z optymistycznego
   odczytu `localStorage`, drugi raz po `getMe` (drugie ustawienie jest zamierzone — to ono podaje
   `NotificationContext` token SignalR; patrz komentarz w `checkLoggedIn`). Trzech konsumentów razy
   dwie tożsamości to sześć pobrań na wejście; sklejanie równoległych żądań w `apiService` ścinało
   z tego tyle, ile akurat leciało w tej samej chwili — a nie to, co leciało PO SOBIE.

   SKUTEK DLA UŻYTKOWNIKA: każde wejście do aplikacji i każde F5 wypuszczało kilka identycznych
   zapytań, zanim jeszcze zdążył kliknąć cokolwiek w menu. Przy kilkuset kontach to właśnie składa
   się na „topornie działa menu".

   JAK TO DZIAŁA TERAZ: jedno pobranie na SESJĘ przeglądarki, wspólne dla wszystkich trzech
   konsumentów, z JAWNYM unieważnieniem. Unieważnia:
     • wylogowanie (`logout` niżej) — obowiązkowo, patrz akapit o bezpieczeństwie;
     • zmiana uprawnień w locie (zdarzenie `uprawnienia-zmienione`);
     • zapis przełącznika w panelu administratora — `UIContext.refreshTicketCreateVisibility()`,
       które panel woła po każdym zapisie wiersza `ticketCreate`, oraz zdarzenie okna
       `module-visibility-changed`, które `pages/admin/ModuleVisibilityPage.js` wysyła po zapisie
       KAŻDEGO wiersza (wpięte 2026-09-12 — wcześniej odświeżał się tylko widget „Nowe zgłoszenie”,
       więc ten nasłuch stał na ścieżce, której produkcja nigdy nie wywoływała).

   BEZPIECZEŃSTWO: widoczność modułu to WARSTWA INTERFEJSU, nie autoryzacja (zasada A15-06 —
   ukrycie zdejmuje wejście, danych broni klucz `PERM:*`). Mimo to pamięć jest czyszczona przy
   wylogowaniu i przy zmianie uprawnień, żeby kolejna osoba na tym samym stanowisku nie zobaczyła
   menu poprzednika ani ustawień sprzed zmiany.

   AWARII NIE ZAPAMIĘTUJEMY: nieudane zapytanie oddaje `{ blad: true }` i NIE ląduje w pamięci —
   inaczej jedno mrugnięcie sieci zamykałoby pozycje modułowe do końca sesji (Sidebar jest w tym
   miejscu fail-closed). Każdy konsument sam decyduje, co robi z `blad` — i zachowuje przy tym
   swoją dotychczasową regułę.
   ═══════════════════════════════════════════════════════════════════════════════════════════════ */

/** Ostatnia znana odpowiedź `/module-visibility` — `{ wiersze }` albo `null` (nic nie wiemy). */
let pamiecWidocznosci = null;
/** Żądanie w locie — kolejni konsumenci doklejają się do niego zamiast pytać drugi raz. */
let wTrakcieWidocznosci = null;
/** Numer pokolenia pamięci; rośnie przy każdym unieważnieniu (odcina spóźnione odpowiedzi). */
let wersjaWidocznosci = 0;
const sluchaczeWidocznosci = new Set();

/** Bieżące pokolenie pamięci — konsument trzyma je w stanie, żeby unieważnienie odpaliło mu efekt. */
export const wersjaPamieciWidocznosciModulow = () => wersjaWidocznosci;

/** Unieważnia pamięć widoczności modułów i budzi wszystkich konsumentów. */
export const wyczyscPamiecWidocznosciModulow = () => {
    pamiecWidocznosci = null;
    wTrakcieWidocznosci = null;
    wersjaWidocznosci += 1;
    sluchaczeWidocznosci.forEach((fn) => {
        try { fn(wersjaWidocznosci); } catch { /* konsument nie może uciszyć pozostałych */ }
    });
};

/** Zapisuje się na unieważnienia pamięci. Zwraca funkcję wypisania (do sprzątnięcia w efekcie). */
export const subskrybujWidocznoscModulow = (fn) => {
    if (typeof fn !== "function") return () => {};
    sluchaczeWidocznosci.add(fn);
    return () => { sluchaczeWidocznosci.delete(fn); };
};

/**
 * Widoczność modułów na tę sesję: `{ wiersze, blad, powod }`.
 * Pierwszy konsument pyta serwer, każdy następny dostaje to samo BEZ sieci.
 */
export const pobierzWidocznoscModulow = () => {
    if (pamiecWidocznosci) return Promise.resolve(pamiecWidocznosci);
    if (!wTrakcieWidocznosci) {
        const mojeWydanie = wersjaWidocznosci;
        wTrakcieWidocznosci = apiService.getModuleVisibility()
            .then((res) => {
                const wynik = { wiersze: Array.isArray(res?.data) ? res.data : [], blad: false };
                // Jeśli w międzyczasie ktoś unieważnił pamięć, ta odpowiedź jest już nieaktualna —
                // oddajemy ją TEMU konsumentowi, ale nie zapisujemy jej jako stanu sesji.
                if (mojeWydanie === wersjaWidocznosci) {
                    pamiecWidocznosci = wynik;
                    wTrakcieWidocznosci = null;
                }
                return wynik;
            })
            .catch((powod) => {
                if (mojeWydanie === wersjaWidocznosci) wTrakcieWidocznosci = null;
                return { wiersze: [], blad: true, powod };
            });
    }
    return wTrakcieWidocznosci;
};

// Unieważnienie „z zewnątrz": panel administratora po zapisie przełącznika (linia do wpięcia
// opisana w raporcie) albo dowolny inny kod, który zmienił tabelę `ModuleVisibilities`.
if (typeof window !== "undefined") {
    window.addEventListener("module-visibility-changed", wyczyscPamiecWidocznosciModulow);
}

/**
 * [BF-1 2026-09-19] DOCIĄGNIĘCIE WIDOKU NAKŁADKI.
 *
 * `podgladWidok` (uprawnienia, moduły i kolory osoby oglądanej) przyjeżdża przy WŁĄCZENIU trybu i żyje
 * w pamięci karty. Po odświeżeniu strony znikał, a `/users/me` niósł tylko sam fakt trwania nakładki —
 * więc powłoka wracała do menu administratora, mimo że serwer od BF-1 odpowiada danymi oglądanej osoby.
 * Skutek na ekranie: menu administratora nad listami pracownika. Awaria tego zapytania NIE MOŻE niczego
 * zablokować — nakładka po prostu zostaje bez dodatkowego opisu, a dane i tak przychodzą z serwera.
 */
const dociagnijWidokPodgladu = async (setPodgladWidok) => {
  try {
    const res = await apiService.getPodgladSesjaWidok();
    setPodgladWidok(res?.data?.aktywny ? (res.data.widok || null) : null);
  } catch {
    /* brak opisu nakładki nie jest powodem, żeby zatrzymać wejście do aplikacji */
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  /* [BC-10, PRZEBUDOWA 2026-09-19] NAKŁADKA „OGLĄDAM KONTO X" — NIE zmiana tożsamości.
     `podglad` = { aktywny, sesjaId, ogladaneUserId, ogladaneNazwa, wygasa, limitMinut, granice }
     albo null. `podgladWidok` = policzony przez serwer widok TAMTEJ osoby (jej uprawnienia, moduły,
     zakładki, kolory) — nakładany niżej na `hasPermission` / `isModuleRestricted` / motyw.

     CO SIĘ ZMIENIŁO I DLACZEGO: wcześniej wejście w podgląd PODMIENIAŁO token na cudzą tożsamość.
     Na produkcji 19.09 skończyło się to tak, że właściciel nie mógł zalogować się na własne konto.
     Teraz `user` NIGDY nie przestaje być administratorem — zmienia się wyłącznie to, co widzi.
     Stan nakładki przyjeżdża razem z tożsamością (`GET /users/me` → pole `podglad`), żeby pasek
     „Oglądasz konto…" był na ekranie od pierwszego renderu. */
  const [podglad, setPodglad] = useState(null);
  const [podgladWidok, setPodgladWidok] = useState(null);
  // Globalnie ukryte moduły menu (ModuleVisibility.isVisible===false) — null = jeszcze nieładowane.
  const [globalHiddenModules, setGlobalHiddenModules] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkLoggedIn();
    // eslint-disable-next-line
  }, []);

  /**
   * [brief 2026-09-03, poz. 10] ODEBRANIE UPRAWNIEŃ DZIAŁA OD RAZU, TAKŻE W INTERFEJSIE.
   *
   * Serwer był gotowy od dawna: odebranie podbija SecurityStamp, bramka tokenu odrzuca stary token
   * przy KAŻDYM żądaniu, a cichy /refresh w `apiService` wydaje nowy token z przeliczonymi
   * uprawnieniami. Brakowało OSTATNIEGO KROKU: świeża lista trafiała do localStorage, ale nie do
   * stanu Reacta — ten ustawiany jest raz, przy montowaniu aplikacji. Skutek: menu boczne, kafelki
   * panelu i bramki tras pokazywały odebrany moduł aż do odświeżenia strony, a kliknięcie kończyło
   * się ekranem „brak uprawnień". Aplikacja zapraszała tam, gdzie już nie wolno wejść.
   *
   * ASYMETRIA JEST ZAMIERZONA (właściciel: „w przypadku nadania może być jak jest"): to zdarzenie
   * przenosi każdą zmianę, ale odebranie dociera natychmiast, bo to ODEBRANIE unieważnia token
   * i wymusza /refresh przy pierwszym żądaniu. Nadanie nie unieważnia niczego i dojdzie przy
   * najbliższej naturalnej okazji — i tak ma być.
   */
  useEffect(() => {
    const naZmianeUprawnien = (e) => {
      const swieze = e?.detail?.permissions;
      if (Array.isArray(swieze)) setPermissions(swieze);
      // [WYDAJNOŚĆ 2026-09-12] Zmiana uprawnień w locie CZYŚCI pamięć powłoki. Zapamiętane
      // odpowiedzi opisują to, co człowiek ma widzieć — po przeliczeniu uprawnień muszą być
      // pobrane na nowo, a nie dożywać sesji w starym kształcie.
      wyczyscPamiecWidocznosciModulow();
      wyczyscPamiecZasiegu();
    };
    window.addEventListener("uprawnienia-zmienione", naZmianeUprawnien);
    return () => window.removeEventListener("uprawnienia-zmienione", naZmianeUprawnien);
  }, []);

  /* [HASLA-LOKALNE, decyzja właściciela 2026-09-27] Serwer odmówił kodem „haslo_do_zmiany” (hasło konta lokalnego wygasło
     w trakcie sesji albo administrator ustawił hasło tymczasowe): zaznaczamy to w stanie konta — ProtectedRoute prowadzi
     wtedy z każdego adresu na ekran zmiany hasła. Flagę zdejmuje dopiero odświeżenie sesji po zmianie (/users/me). */
  useEffect(() => {
    const naHasloDoZmiany = (e) => {
      setUser((u) => (u && u.hasloDoZmiany !== true
        ? { ...u, hasloDoZmiany: true, powodZmianyHasla: e?.detail?.powod || u.powodZmianyHasla || null }
        : u));
    };
    window.addEventListener(ZDARZENIE_HASLO_DO_ZMIANY, naHasloDoZmiany);
    return () => window.removeEventListener(ZDARZENIE_HASLO_DO_ZMIANY, naHasloDoZmiany);
  }, []);

  // Numer pokolenia pamięci widoczności — unieważnienie podbija go i odpala efekt niżej.
  const [wersjaWidocznosciModulow, setWersjaWidocznosciModulow] = useState(wersjaPamieciWidocznosciModulow);
  useEffect(() => subskrybujWidocznoscModulow(setWersjaWidocznosciModulow), []);

  // Po zalogowaniu pobierz globalną widoczność modułów (do blokady tras, nie tylko menu).
  // [WYDAJNOŚĆ 2026-09-12] Przez pamięć sesji (patrz nagłówek pliku): pierwszy z trzech konsumentów
  // powłoki pyta serwer, dwaj pozostali — i drugie ustawienie `user` po `getMe` — czytają bez sieci.
  // Awaria zostaje przy dotychczasowej decyzji: pusty zbiór ukrytych, czyli fail-OPEN (trasy i tak
  // pilnuje `PERM:*`; menu ma własną, ostrzejszą regułę i samo ją stosuje).
  useEffect(() => {
    if (!user) { setGlobalHiddenModules(null); return undefined; }
    let cancelled = false;
    pobierzWidocznoscModulow().then(({ wiersze }) => {
      if (cancelled) return;
      setGlobalHiddenModules(new Set(wiersze.filter(m => m.isVisible === false).map(m => m.moduleCode)));
    });
    return () => { cancelled = true; };
  }, [user, wersjaWidocznosciModulow]);

  const checkLoggedIn = async () => {
    // Tokeny są w httpOnly cookies — nie sprawdzamy localStorage.accessToken
    const storedUser = localStorage.getItem("user");
    const storedPerms = localStorage.getItem("permissions");

    // 1) Optymistyczny, szybki render z localStorage (cache pierwszego renderu)
    if (storedUser) {
      try { setUser(JSON.parse(storedUser)); } catch { setUser(null); }
      if (storedPerms) {
        try {
          setPermissions(JSON.parse(storedPerms));
        } catch (e) {
          setPermissions([]);
        }
      }
    }
    setLoading(false);

    // 2) Weryfikacja sesji + odświeżenie danych z serwera (źródło prawdy) oraz tokenu SignalR.
    //    Dzięki temu po odświeżeniu strony (F5) token SignalR jest ustawiony i powiadomienia
    //    real-time działają, a uprawnienia/typ pochodzą z backendu, nie tylko z localStorage.
    if (storedUser) {
      try {
        // Czy token SignalR był już ustawiony PRZED getMe (np. świeżo po logowaniu)?
        const hadSignalRToken = !!getSignalRToken();
        const res = await apiService.getMe();
        const { accessToken, user: userData, permissions: userPerms } = res.data;
        // [BC-10] Nakładka: serwer mówi, czy ta osoba ma włączony podgląd cudzego widoku.
        const nakladka = res.data?.podglad?.aktywny ? res.data.podglad : null;
        setPodglad(nakladka);
        /* [BF-1 2026-09-19] Po odświeżeniu strony (F5) `podgladWidok` nie istnieje — żyje w pamięci karty.
           Bez dociągnięcia go tutaj nakładka po F5 gasła po stronie przeglądarki, mimo że serwer dalej
           odpowiadał danymi oglądanej osoby: menu wracało administratorowi, a listy pod nim zostawały jej.
           Dokładnie ten rodzaj rozjazdu, w którym każdy koniec wygląda poprawnie z osobna. */
        if (!nakladka) setPodgladWidok(null); else await dociagnijWidokPodgladu(setPodgladWidok);
        if (accessToken) setSignalRToken(accessToken);
        const safePerms = userPerms || [];
        const freshUserJson = JSON.stringify(userData);
        const freshPermsJson = JSON.stringify(safePerms);
        localStorage.setItem("user", freshUserJson);
        localStorage.setItem("permissions", freshPermsJson);
        // Aktualizuj stan gdy dane się zmieniły LUB gdy token SignalR dopiero się pojawił
        // (po F5 efekt w NotificationContext musi dostać drugi przebieg z już dostępnym tokenem;
        // bez zmiany tożsamości `user` połączenie real-time nigdy by nie wstało).
        if (freshUserJson !== storedUser || !hadSignalRToken) {
          setUser({
            ...userData,
            jobPosition: userData.positionId || 10,
            employeeNumber: userData.employeeNumber || null,
          });
        }
        if (freshPermsJson !== (storedPerms || "[]")) {
          setPermissions(safePerms);
        }
      } catch (e) {
        // 401 obsłuży interceptor (refresh lub redirect na login).
        // Błąd sieci — zostają dane z localStorage do czasu odzyskania połączenia.
      }
    }
  };

  // Funkcja pomocnicza do ustawiania stanu po udanym logowaniu
  const handleLoginSuccess = (accessToken, userData, userPerms) => {
    // NIE zapisujemy tokenów do localStorage — httpOnly cookies ustawione przez backend
    // Zapisujemy tylko user data i permissions (nie są sekretem)
    localStorage.setItem("user", JSON.stringify(userData));

    const safePerms = userPerms || [];
    localStorage.setItem("permissions", JSON.stringify(safePerms));

    // SignalR token — trzymany w pamięci (nie localStorage)
    if (accessToken) setSignalRToken(accessToken);

    setUser({
      ...userData,
      jobPosition: userData.positionId || 10,
      employeeNumber: userData.employeeNumber || null,
    });
    setPermissions(safePerms);

    // Diagnostyka: LOCAL user z org email
    if (!userData.domain) {
      try {
        const raw = localStorage.getItem("itsm_org_domains_cache");
        if (raw) {
          const { domains } = JSON.parse(raw);
          const emailDomain = userData.email?.split("@")[1]?.toLowerCase();
          if (domains?.some(d => d.domain === emailDomain)) {
            // org domain user detected - handled by account linking service
          }
        }
      } catch { /* ignore */ }
    }

    navigate("/");
  };

  // Standardowe logowanie
  //
  // Żądanie idzie przez WSPÓLNĄ instancję axios (cały łańcuch interceptorów: down-detektor,
  // unieważnianie pamięci, 429), ale z `KANAL_LOGOWANIA` — patrz komentarz przy tej stałej.
  // Bez tego 401 „Konto jest zablokowane." wracało do wywołującego jako 400 „Access token and
  // refresh token are required." z nieudanego odświeżania sesji.
  const login = useCallback(async (formData) => {
    try {
      const res = await apiService.api.post("/users/login", formData, KANAL_LOGOWANIA);
      const { accessToken, user: userData, permissions: userPerms } = res.data;

      handleLoginSuccess(accessToken, userData, userPerms);
      return { success: true };
    } catch (err) {
      return odmowaLogowania(err, "serwer", "/users/login");
    }
  }, []);

  // Logowanie Office 365
  const loginOffice365 = useCallback(async (token) => {
    try {
      const res = await apiService.api.post("/users/login-office365", { idToken: token }, KANAL_LOGOWANIA);
      const { accessToken, user: userData, permissions: userPerms } = res.data;

      // Flaga auto-connect — DashboardPage odczyta po załadowaniu i uruchomi OAuth
      if (userData?.email) {
        localStorage.setItem("pendingMsAutoConnect", userData.email);
      }

      handleLoginSuccess(accessToken, userData, userPerms);
      return { success: true };
    } catch (err) {
      // Powód odmowy (np. „Konfiguracja dla tego tenanta O365 jest nieaktywna lub nie istnieje.")
      // idzie na ekran, pełny ślad do dziennika przeglądarki i do pamięci — patrz `odmowaLogowania`.
      return odmowaLogowania(err, "serwer", "/users/login-office365");
    }
  }, []);

  /**
   * [DOSTĘP 2026-07-27] Ponowne pobranie tożsamości i uprawnień BEZ wylogowania.
   *
   * Potrzebne, gdy administrator zmieni uprawnienia WŁASNEGO konta: zapis unieważnia stary token
   * (SecurityStamp), ale świeże uprawnienia trafiają do przeglądarki dopiero przy kolejnym
   * logowaniu — więc panel pokazywał „zapisano", a moduł dalej odbijał ekranem „brak uprawnień".
   * Tu wymuszamy odczyt z serwera (interceptor po drodze odświeży token) i podmieniamy stan.
   */
  const refreshSession = useCallback(async () => {
    const res = await apiService.getMe();
    const { accessToken, user: userData, permissions: userPerms } = res.data;
    const nakladka = res.data?.podglad?.aktywny ? res.data.podglad : null;
    setPodglad(nakladka);
    if (!nakladka) setPodgladWidok(null); else await dociagnijWidokPodgladu(setPodgladWidok);
    const safePerms = userPerms || [];
    if (accessToken) setSignalRToken(accessToken);
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("permissions", JSON.stringify(safePerms));
    setUser({
      ...userData,
      jobPosition: userData.positionId || 10,
      employeeNumber: userData.employeeNumber || null,
    });
    setPermissions(safePerms);
    return safePerms;
  }, []);

  /* [BC-10, polecenie właściciela 2026-09-18] WEJŚCIE W TRYB „ZALOGUJ JAKO".
     Serwer podmienia ciasteczko dostępowe na token sesji podglądu (tożsamość osoby oglądanej +
     znacznik podglądu) i odsyła jej uprawnienia. Tu zostaje jedno zadanie: przeładować powłokę
     Serwer zakłada wiersz sesji i ODDAJE POLICZONY WIDOK tamtej osoby. Tożsamości nie ruszamy:
     `user`, tokeny i ciasteczka zostają administratora. Dlatego NIE przeładowujemy aplikacji i NIE
     czyścimy localStorage — nakładka wchodzi w locie, bo powłoka pyta o uprawnienia i widoczność
     przez ten kontekst, a on od tej chwili odpowiada „jak dla oglądanej osoby". */
  const wejdzWPodglad = useCallback(async (userId) => {
    try {
      const res = await apiService.startPodgladSesji(userId);
      setPodglad({ aktywny: true, ...(res?.data?.sesja || {}), granice: res?.data?.granice || [] });
      setPodgladWidok(res?.data?.widok || null);
      /* [BF-1 2026-09-19] DANE TEŻ MUSZĄ BYĆ JEJ. Właściciel: „nawet dashboard ma być jej wczytany
         i widoki w każdej zakładce". Od BF-1 serwer w tym trybie odpowiada danymi osoby oglądanej,
         więc `user` i `permissions` w powłoce muszą zostać przeczytane NA NOWO — inaczej pulpit witałby
         dalej administratora z jego licznikami, a pasek u góry obiecywałby cudzy widok. */
      await refreshSession().catch(() => {});
      return { success: true };
    } catch (err) {
      const d = err?.response?.data;
      return { success: false, message: (typeof d === "string" ? d : d?.message) || "Nie udało się włączyć podglądu tego konta." };
    }
  }, [refreshSession]);

  /* „Wróć do swojego widoku".
     🔴 [BF-1 2026-09-19] ZMIANA WOBEC POPRZEDNIEJ WERSJI — i powód, dla którego jest odwrotnie niż było.
     BYŁO: przy błędzie sieci gasiliśmy nakładkę TYLKO LOKALNIE („przycisk, który nie ma prawa nie
     zadziałać"). Miało to sens, dopóki nakładka zmieniała wyłącznie UKŁAD — czyli gdy dane w modułach
     i tak były administratora. Od BF-1 serwer w tym trybie odpowiada DANYMI OSOBY OGLĄDANEJ, więc
     zgaszenie samego paska przy otwartym wierszu sesji dałoby stan najgorszy z możliwych: cudze dane
     na ekranie BEZ ŻADNEJ informacji, że to cudze dane.
     JEST: prosimy serwer o zakończenie, a potem PYTAMY GO, jak jest naprawdę (`refreshSession` →
     `/users/me`). Jeżeli sesja faktycznie zgasła (typowy przypadek: odpowiedź zginęła, żądanie doszło)
     — wracają dane administratora. Jeżeli nie zgasła — pasek zostaje i mówi o tym wprost.
     CZY MOŻNA UTKNĄĆ: nie. Tożsamość sesji przez cały czas należy do administratora, więc zostaje
     ponowienie, wylogowanie (działa zawsze) i twardy limit 30 minut. */
  const wrocNaSwojeKonto = useCallback(async () => {
    let bladSerwera = null;
    let sciezkaPowrotu = null;
    try {
      /* [polecenie wlasciciela 2026-09-19] „jak wracam do swojego widoku, to przywracaj mnie do miejsca
         gdzie bylem wczesniej". Adres przyszedl z SERWERA (zapamietany przy wejsciu), wiec powrot dziala
         takze po odswiezeniu strony i z drugiej karty. */
      const res = await apiService.zakonczPodgladSesji();
      sciezkaPowrotu = res?.data?.sciezkaPowrotu || null;
    } catch (err) {
      const d = err?.response?.data;
      bladSerwera = (typeof d === "string" ? d : d?.message)
        || "Serwer nie potwierdził wyjścia z trybu podglądu. Sprawdzam, jak jest naprawdę…";
    }

    setPodglad(null);
    setPodgladWidok(null);
    /* [BF-1] Po zgaszeniu nakładki `user` i `permissions` w pamięci karty należą jeszcze do osoby
       oglądanej (bo /me w trybie podglądu oddaje JEJ dane). Ten odczyt jest jedynym miejscem, które
       zna prawdę o stanie sesji — i jednocześnie przywraca administratorowi jego własny pulpit. */
    const stan = await refreshSession().then(() => "ok").catch(() => "blad");

    if (!bladSerwera) return { success: true, sciezkaPowrotu };
    return {
      success: true,
      sciezkaPowrotu,
      message: stan === "ok"
        ? bladSerwera + " Jeżeli pasek nadal jest widoczny, tryb wciąż trwa — spróbuj ponownie albo się wyloguj."
        : bladSerwera + " Nie udało się też odświeżyć sesji — odśwież stronę albo się wyloguj.",
    };
  }, [refreshSession]);

  // [KA-05 2026-09-26] `logout({ wszedzie: true })` = „Wyloguj ze wszystkich urządzeń” (POST /users/logout-all).
  // Zwykłe wywołanie (także z onClick, gdzie pierwszym argumentem jest zdarzenie) kończy tylko to urządzenie.
  const logout = useCallback((opcje) => {
    /* [BC-10, przebudowa 2026-09-19] WYLOGOWANIE DZIAŁA ZAWSZE — TAKŻE Z WŁĄCZONĄ NAKŁADKĄ.
       Wcześniej stał tu warunek zamieniający „Wyloguj" na „wróć na swoje konto", bo POST /users/logout
       wpadał w bramkę „tylko odczyt". To był objaw, nie przyczyna: przyczyną było przejęcie sesji.
       Teraz drogi uwierzytelnienia są przepuszczane bezwarunkowo (TrybPodgladuKonta), a tożsamość
       i tak należy do administratora — więc wylogowanie jest zwykłym wylogowaniem. Gasimy przy okazji
       nakładkę w stanie, żeby ekran logowania nie pokazał paska „Oglądasz konto…". */
    if (podglad?.aktywny) { apiService.zakonczPodgladSesji().catch(() => {}); }
    setPodglad(null);
    setPodgladWidok(null);
    // Backend czyta refresh token z httpOnly cookie — nie trzeba go wysyłać w body
    const wszedzie = opcje && opcje.wszedzie === true && typeof apiService.logoutAll === "function";
    (wszedzie ? apiService.logoutAll() : apiService.logout()).catch(() => {});
    // Czyścimy tylko user data z localStorage (tokeny są w cookies, backend je czyści)
    // [A15-10] Do listy dołączają STARE, globalne klucze kropek menu. Nowy kod pisze już wyłącznie
    // pod kluczem z identyfikatorem konta (useSidebarBadges), ale na stacjach sprzed aktualizacji
    // leży jeszcze wspólny zestaw „odwiedzin" poprzedniej osoby. SKUTEK DLA UŻYTKOWNIKA: dopóki tam
    // leżał, kolejny zalogowany na wspólnym stanowisku mógł odczytać, które sekcje odwiedzał
    // poprzednik. Usuwamy je przy świadomym wylogowaniu. Kluczy per konto NIE ruszamy — ta sama
    // osoba po ponownym zalogowaniu ma odzyskać swoje wygaszone kropki.
    // Nazwy wpisane wprost, a nie zaimportowane z useSidebarBadges — ten hook czyta useAuth,
    // który czyta ten kontekst, więc import zamknąłby cykl. Zgodność obu miejsc pilnuje test
    // "AuthContext.logout czyści STARE globalne klucze kropek" w AuthContext.logout.test.js.
    [
      "user", "permissions", "pendingMsAutoConnect",
      "sidebar_last_visited_v2", "sidebar_latest_content_v2",
      // [PII-09, przegląd bezpieczeństwa 2026-09-26] BYŁO ŹLE: ostatnie 8 fraz wyszukiwarki globalnej
      // (często nazwiska: „Kowalski L4") leżało pod jednym kluczem przeglądarki, niezależnym od konta.
      // SKUTEK DLA UŻYTKOWNIKA: na wspólnym stanowisku kolejna osoba otwierała wyszukiwarkę i widziała,
      // kogo szukał poprzednik. NAPRAWA: świadome wylogowanie zabiera historię (jak A15-10 i A16-06).
      KLUCZ_HISTORII,
    ].forEach(k => localStorage.removeItem(k));
    // [A16-06] BYŁO: dane kadrowe e-teczki (offboarding, szkic świadectwa pracy) zostawały
    // w localStorage po wylogowaniu — na współdzielonej stacji czytał je kolejny użytkownik
    // przeglądarki, mimo że moduł jest za uprawnieniem. Świadome wylogowanie musi je usunąć.
    clearSensitiveHrStorage();
    /* [WYDAJNOŚĆ 2026-09-12] PAMIĘĆ POWŁOKI ZNIKA RAZEM Z SESJĄ.
       Powłoka trzyma teraz w pamięci modułu dwie odpowiedzi opisujące ZAKRES WIDZENIA człowieka:
       widoczność modułów menu i zasięg „Mojego Działu". Bez tego skasowania kolejna osoba logująca
       się w TEJ SAMEJ karcie (wspólne stanowisko: hala, recepcja, komputer zmianowy) dostałaby menu
       i zakładki działu policzone dla poprzednika — do pierwszego F5. Zasięg działu nie był dotąd
       czyszczony NIGDY (funkcja `wyczyscPamiecZasiegu` istniała od N-6, ale nikt jej nie wołał);
       to jest domknięcie tamtej luki, nie tylko obsługa nowej pamięci.
       Zdarzenie `sesja-zakonczona` gasi pamięci trzymane w plikach, które nie mogą zaimportować
       tego kontekstu bez zamknięcia cyklu (np. lista konkursów jury w `Sidebar`). */
    wyczyscPamiecWidocznosciModulow();
    wyczyscPamiecZasiegu();
    try { window.dispatchEvent(new Event("sesja-zakonczona")); } catch { /* brak okna w teście */ }
    setSignalRToken(null);
    setUser(null);
    setPermissions([]);
    navigate(localizedPath("login"));
  }, [navigate, podglad]);

  // Uprawnienia modułowe — admin/ALL_ACCESS NIE daje ich automatycznie.
  // Zarządzane w /admin/app-access przełącznikami modułów.
  const EXPLICIT_ONLY_PERMS = [
      // Obsługa IT
      "TicketView", "TicketCreate", "TicketResponse", "TicketAssign",
      "TicketChangeStatus", "TicketClose", "TicketEditPriority", "TicketEditPlannedDate",
      /* [zgłoszenie właściciela 2026-09-11] Scalanie zgłoszeń. Klucz MUSI tu stać, bo lista jest
         lustrem backendowego `PermissionScopeRules.ExplicitOnly`: bez wpisu front uznałby, że
         administrator ma go z rangi, pokazał przycisk „Scal" — a API i tak by odmówiło. Martwy
         przycisk przy czynności NIEODWRACALNEJ jest gorszy niż brak przycisku. */
      "TicketMerge",
      // Obsługa HR
      "HrAccess",
      // [DELEGACJE 2026-09-12] Księgowość — kolejka rozliczeń delegacji. Lustro PermissionScopeRules.ExplicitOnly:
      // bez wpisu front uznałby, że administrator ma klucz z rangi, i pokazał kolejkę, której API by odmówiło.
      "HrAccounting",
      // [OBSZAR 18 2026-09-18] Podglad cudzego konta (zaloguj jako bez wchodzenia na konto). Lustro
      // PermissionScopeRules.ExplicitOnly: bez wpisu front uznalby, ze administrator ma klucz z rangi,
      // zapalil kafelek w Systemie i integracjach — a API odmawia, bo wymaga imiennego nadania.
      "PodgladKontaAdmin",
      // [SYGNALISTA] Poufnosc: nawet admin/ALL_ACCESS nie widzi modulu bez jawnego klucza
      // (czlonkowie dzialu obslugujacego dostaja go automatycznie przy logowaniu).
      "SygnalistaManage",
      // [SPK-09, 2026-09-27] Koordynator compliance — obsada spolek sygnalisty. Lustro PermissionScopeRules.ExplicitOnly:
      // bez wpisu front pokazalby administratorowi zakladke „Obsada spolek”, ktorej API mu odmawia.
      "SygnalistaObsadaSpolek",
      // [F-8 2026-09-11] Menedzer hasel — wglad centralny. Ranga administratora nie otwiera
      // firmowych bankow hasel, a sejfu osobistego pracownika nie otwiera nic.
      "SejfCentralnyWglad",
      // [polecenie wlasciciela 2026-10-03] Import hasel z pliku Excel do bankow dzialow. Lustro PermissionScopeRules.ExplicitOnly:
      // bez wpisu front uznalby, ze administrator ma klucz z rangi, i pokazal przycisk „Importuj z Excela”, ktorego API odmawia.
      "SejfImportBankow",
      // [SPRAWY HR 2026-08-30] Sprawy kadrowe w silniku zgłoszeń — ten sam wzorzec need-to-know
      // co przy sygnaliście: administrator IT nie czyta sprawy płacowej ani skargi pracownika
      // tylko dlatego, że ma pełny dostęp do systemu. Lista musi być zgodna z PermissionCatalog
      // i UsersController.ExplicitOnlyPermissions — pilnuje tego AuthContext.test.js (A15-12).
      "HrCaseHandle",
      "HrCaseSensitive",
      // E-Teczka
      "ModuleETeczka",
      // Mój Dział
      "ModuleMojDzial", "DepartmentManagerAccess", "DepartmentViewUsers", "DepartmentAssignUsers",
      // [PAKIET 2026-09-02] Dokumenty kadrowe, onboarding, offboarding — ten sam wzorzec need-to-know:
      // wydanie umowy i prowadzenie sprawy zatrudnienia sięgają po dane osobowe i warunki płacowe,
      // więc ranga administratora ich NIE otwiera. (ItOnboardHandle celowo poza listą — to projekcja
      // techniczna dla IT, bez danych kadrowych.)
      "HrDocIssue",
      "HrOnboardView",
      "HrOnboardManage",
      "HrOffboardView",
      "HrOffboardManage",
      // [PAKIET 2026-09-02] Weryfikacja celów przez kadry: ocena okresowa pracownika i uzasadnienie
      // oceny to opinia o człowieku, nie dane operacyjne — administrator IT nie czyta jej z rangi.
      "GoalsHrReview",
      // [PAKIET 2026-09-02] Kandydaci i CV. Aplikacja o pracę bywa złożona przez własnego pracownika,
      // a jej ujawnienie przełożonemu potrafi kosztować człowieka stanowisko — dlatego wzorzec jest
      // ostrzejszy niż przy pozostałych kluczach: dostęp WYŁĄCZNIE imienny, bez dziedziczenia z działu.
      "JobCandidatesView",
      "JobCandidatesManage",
      // [polecenie właściciela 2026-09-06, pkt 4] Raport pracy zdalnej. Klucz powstał po to, żeby dostęp
      // do dziennych raportów dało się nadać OSOBNO, bez oddawania całej obsługi kadr — gdyby otwierała
      // go ranga, naprawa zamieniłaby się w kolejne „drugie drzwi".
      "HrRemoteWorkReports",
      // [POLITYKI 2026-09-28, E7 D26] Lustro PermissionScopeRules.ExplicitOnly: panel potwierdzeń z IP/UA pracowników i
      // rejestracje DOWODÓW (decyzje zarządu, potwierdzenia papierowe). Bez wpisu front uznałby, że administrator ma je
      // z rangi, pokazał zakładki — a API odmawia (NoAdminRankBypass).
      // [POLITYKI-poprawka 2026-09-29] PolitykiRedakcja DOŁĄCZA (lustro PermissionScopeRules): menu i trasa /polityki pokazywały
      // administratorowi wejście z rangi, a predykat wersji roboczych (§4, bez rangi) nie pokazałby mu własnych szkiców.
      "PolitykiRedakcja",
      "PolitykiRaportPotwierdzen",
      "PolitykiRejestrujDecyzje",
      "PolitykiRejestrujPotwierdzenia",
      // [POLITYKI-poprawka 2 2026-09-29] Zmiana ustawień modułu polityk — osobny klucz, imiennie, bez rangi (rozdział obowiązków).
      "PolitykiUstawienia",
      // [E8 2026-09-30, pakiet P5, E8 D43] Wykaz KSC — lustro PermissionScopeRules.ExplicitOnly: wpis do Wykazu to mapa infrastruktury
      // i osób (IP, domeny, telefony, administratorzy S46). Bez wpisu front uznałby, że administrator ma klucze z rangi, i pokazał
      // pulpit /wykaz-ksc, którego API odmawia (NoAdminRankBypass). Atestacja dodatkowo tylko imiennie (NeverInheritedFromDepartment).
      "WykazKscPodglad",
      "WykazKscRedakcja",
      "WykazKscAtestacja",
      // [O365-F 2026-10-04, decyzja właściciela 3.10] Zarządzanie Office 365 — lustro PermissionScopeRules.ExplicitOnly: konta tenantów, MFA,
      // role i historia logowań osób. Ranga administratora ich nie otwiera (NoAdminRankBypass); wejście = kafelek modułowy acc_office365.
      // Bez wpisu front uznałby, że administrator ma klucze z rangi, i otworzył zakładkę, której API odmawia.
      "Office365View",
      "Office365Manage",
  ];

  /* ═══════════════════════════════════════════════════════════════════════════════════════════
     [BC-10, PRZEBUDOWA 2026-09-19] NAKŁADKA WIDOKU — „na moim koncie widzę czyjś widok 1 do 1".

     Poniżej JEDYNE miejsce, w którym podgląd zmienia zachowanie aplikacji. Nie zmienia tożsamości:
     `user` i `permissions` dalej opisują administratora, a zmienia się odpowiedź na trzy pytania,
     którymi powłoka rysuje wejścia — `hasPermission`, `isModuleRestricted`, `isModuleGloballyHidden`.
     Z tego bierze się „jej menu, jej kafelki, jej zakładki, jej sprawdzenie dostępu": Sidebar,
     siatka /admin i `ProtectedRoute` pytają dokładnie tych trzech funkcji.

     CZEGO NAKŁADKA NIE ROBI — i trzeba to mówić wprost, bo inaczej byłaby kłamstwem: NIE podmienia
     DANYCH w modułach. Żądania idą z tożsamością administratora, więc „Moje pliki" pokazują jego
     pliki. Widok tamtej osoby (uprawnienia ze źródłem, działy, PIN, zamaskowane dane osobowe) jest
     pokazywany na ekranie podglądu konta i w tym, co wraca z `GET /podglad-sesja/widok`.
     ═══════════════════════════════════════════════════════════════════════════════════════════ */
  const nakladkaAktywna = !!podglad?.aktywny && !!podgladWidok;

  /** Klucze oglądanej osoby — zbiór, żeby `hasPermission` nie przemiatał tablicy przy każdym pytaniu. */
  const kluczePodgladu = useMemo(
    () => new Set((podgladWidok?.uprawnienia || []).map((u) => u.key)),
    [podgladWidok],
  );

  const ukryteWPodgladzie = useMemo(() => {
    const m = new Map();
    for (const x of podgladWidok?.moduly || []) m.set(x.code, x);
    return m;
  }, [podgladWidok]);

  const hasPermission = useCallback((requiredPerm) => {
    if (!user) return false;

    if (!requiredPerm) return true;

    /* [BC-10] NAKŁADKA: odpowiadamy tak, jak odpowiedzielibyśmy OGLĄDANEJ osobie. Klucze z serwera są
       już EFEKTYWNE (reguła logowania dołożyła rangę i ALL_ACCESS, odjęła odebrania), więc nie wolno
       tu dokładać skrótu administratora — pokazałby wejście, którego tamta osoba nie ma. */
    if (nakladkaAktywna) {
      const wymagane = Array.isArray(requiredPerm) ? requiredPerm : [requiredPerm];
      return wymagane.some((p) => kluczePodgladu.has(p));
    }

    // [brief 2026-09-01, pkt L7] UPRAWNIENIA JAWNIE ODEBRANE — sprawdzane PIERWSZE, przed skrótem
    // rangi administratora. Właściciel: „nawet admin nie może mieć wszędzie wglądu".
    // Bez tego warunku front pokazywałby administratorowi z odebranym kluczem pozycję w menu
    // i kafelek w panelu, a backend odbijałby go odmową — czyli aplikacja obiecywałaby dostęp,
    // którego sama nie daje. Lista jest liczona serwerowo i przyjeżdża razem z sesją.
    const denied = Array.isArray(user.deniedPermissions) ? user.deniedPermissions : [];
    if (denied.length > 0) {
      const wymagane = Array.isArray(requiredPerm) ? requiredPerm : [requiredPerm];
      // Tablica kluczy znaczy „wystarczy którykolwiek" — odmowa dotyczy więc sytuacji,
      // w której odebrano WSZYSTKIE alternatywy.
      if (wymagane.every(p => denied.includes(p))) return false;
    }

    // Sprawdź czy wymagane uprawnienie wymaga jawnego nadania.
    // Zachowawczo: jeśli CHOĆ JEDEN element tablicy jest explicit-only, nie stosujemy skrótu admina
    // (admin i tak przejdzie przez zwykłe uprawnienie, jeśli je ma — patrz OR niżej).
    const isExplicitOnly = Array.isArray(requiredPerm)
        ? requiredPerm.some(p => EXPLICIT_ONLY_PERMS.includes(p))
        : EXPLICIT_ONLY_PERMS.includes(requiredPerm);

    // Admin (typ 3) lub flaga ALL_ACCESS — dostęp do wszystkiego POZA explicit-only
    // Skrót rangi/ALL_ACCESS nie dotyczy kluczy, które odebrano jawnie — sprawdzone wyżej.
    if (!isExplicitOnly && (user.type === 3 || permissions.includes("ALL_ACCESS"))) return true;

    if (Array.isArray(requiredPerm)) {
        return requiredPerm.some(p => permissions.includes(p) && !denied.includes(p));
    }

    return permissions.includes(requiredPerm);
  }, [user, permissions, nakladkaAktywna, kluczePodgladu]);

  const isAdmin = user?.type === 3;

  // Czy moduł menu jest ukryty dla tego użytkownika (deny-list per user).
  // Domyślnie nic nie jest ukryte (brak regresji). Admin również podlega ukryciom menu
  // (to widoczność osobista, nie uprawnienie) — ale to admin sam je sobie nakłada.
  // [S-6, polecenie właściciela 2026-09-09] Moduły, których konto NIE dostaje przez formę zatrudnienia (lista z sesji:
  // user.contractGatedModules — liczy ją backend z konfiguratora kadr). Osobny predykat, bo ekran odmowy ma inny komunikat.
  const isModuleContractGated = useCallback((code) => {
    if (!code || !user) return false;
    // [BC-10] Nakładka: forma zatrudnienia OGLĄDANEJ osoby (serwer liczy ją z konfiguratora kadr).
    if (nakladkaAktywna) {
      const lista = Array.isArray(podgladWidok?.contractGatedModules) ? podgladWidok.contractGatedModules : [];
      return lista.includes(code) || !!ukryteWPodgladzie.get(code)?.pozaFormaZatrudnienia;
    }
    const list = Array.isArray(user.contractGatedModules) ? user.contractGatedModules : [];
    return list.includes(code);
  }, [user, nakladkaAktywna, podgladWidok, ukryteWPodgladzie]);

  const isModuleRestricted = useCallback((code) => {
    if (!code || !user) return false;
    // [BC-10] Nakładka: ukrycia OGLĄDANEJ osoby (per konto + forma zatrudnienia) — to one decydują,
    // czy zobaczy pozycję w menu i czy wejdzie na trasę. Ukrycie globalne ma osobny predykat niżej.
    if (nakladkaAktywna) {
      const lista = Array.isArray(podgladWidok?.restrictedModules) ? podgladWidok.restrictedModules : [];
      return lista.includes(code) || isModuleContractGated(code)
        || !!ukryteWPodgladzie.get(code)?.ukrytyDlaKonta;
    }
    const list = Array.isArray(user.restrictedModules) ? user.restrictedModules : [];
    // Forma zatrudnienia gasi wejście (menu, pulpit, trasę) TĄ SAMĄ drogą co ukrycie modułu — każdy konsument
    // isModuleRestricted dostaje to za darmo; backend i tak odmawia niezależnie od frontu.
    return list.includes(code) || isModuleContractGated(code);
  }, [user, isModuleContractGated, nakladkaAktywna, podgladWidok, ukryteWPodgladzie]);

  // Czy moduł jest WYŁĄCZONY GLOBALNIE (ModuleVisibility dla wszystkich). Dopóki nieładowane
  // (null) — nie blokujemy (fail-open na krótkim oknie startu; menu i tak go nie pokaże).
  const isModuleGloballyHidden = useCallback((code) => {
    if (!code) return false;
    // [BC-10] Nakładka: ukrycie globalne jest wspólne dla firmy, ale bierzemy je z widoku oglądanej
    // osoby — dzięki temu ekran pokazuje DOKŁADNIE ten sam zestaw, który policzył serwer, i nie zależy
    // od tego, czy pamięć widoczności modułów zdążyła się już wczytać.
    if (nakladkaAktywna) return !!ukryteWPodgladzie.get(code)?.globalnieUkryty;
    if (!globalHiddenModules) return false;
    return globalHiddenModules.has(code);
  }, [globalHiddenModules, nakladkaAktywna, ukryteWPodgladzie]);

  const value = useMemo(() => ({
    user,
    permissions,
    loading,
    isAdmin,
    login,
    loginOffice365,
    logout,
    refreshSession,
    hasPermission,
    isModuleRestricted,
    isModuleContractGated,
    isModuleGloballyHidden,
    // [BC-10] Nakładka „oglądam konto X": stan, policzony widok tamtej osoby, wejście i wyjście.
    podglad,
    podgladWidok,
    podgladAktywny: nakladkaAktywna,
    wejdzWPodglad,
    wrocNaSwojeKonto
  }), [user, permissions, loading, isAdmin, login, loginOffice365, logout, refreshSession, hasPermission, isModuleRestricted, isModuleContractGated, isModuleGloballyHidden, podglad, podgladWidok, nakladkaAktywna, wejdzWPodglad, wrocNaSwojeKonto]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
